import LegalPageLayout, {
  type LegalSection,
  type TocItem,
} from "@/components/landing/shared/LegalPageLayout";
import CanonicalTags from "@/components/seo/CanonicalTags";

const TOC: TocItem[] = [
  { num: "01", title: "Acceptance of Terms", id: "section-acceptance" },
  { num: "02", title: "Eligibility", id: "section-eligibility" },
  { num: "03", title: "Account Registration", id: "section-account" },
  { num: "04", title: "Description of Services", id: "section-services" },
  { num: "05", title: "Subscriptions and Billing", id: "section-billing" },
  { num: "06", title: "Acceptable Use", id: "section-use" },
  { num: "07", title: "User Content", id: "section-content" },
  { num: "08", title: "Career Intent Data", id: "section-career-data" },
  { num: "09", title: "Intellectual Property", id: "section-ip" },
  { num: "10", title: "Third-Party Services", id: "section-third-party" },
  { num: "11", title: "Disclaimers", id: "section-disclaimers" },
  { num: "12", title: "Limitation of Liability", id: "section-liability" },
  { num: "13", title: "Indemnification", id: "section-indemnification" },
  { num: "14", title: "Termination", id: "section-termination" },
  { num: "15", title: "Governing Law", id: "section-governing-law" },
  { num: "16", title: "Changes to These Terms", id: "section-changes" },
  { num: "17", title: "Contact Us", id: "section-contact" },
];

const SECTIONS: LegalSection[] = [
  {
    num: "01",
    title: "Acceptance of Terms",
    id: "section-acceptance",
    body: (
      <>
        <p>
          By accessing or using the Compaire platform ("Platform"), you agree to be bound by these
          Terms of Service ("Terms"). Please read these Terms carefully before using the Platform.
          These Terms constitute a legally binding agreement between you and Compaire, Inc.
          ("Compaire," "we," "us," or "our").
        </p>
        <div className="legal-callout">
          <div className="legal-callout-eyebrow">Important</div>
          <p>
            If you do not agree to these Terms, do not use the Platform. Continued use of the
            Platform after any changes to these Terms constitutes acceptance of the revised Terms.
          </p>
        </div>
        <p>
          These Terms apply to all users of the Platform, including real estate agents, team
          leaders, brokers, and organizational representatives accessing the Platform in any
          capacity.
        </p>
      </>
    ),
  },
  {
    num: "02",
    title: "Eligibility",
    id: "section-eligibility",
    body: (
      <>
        <p>To access and use the Platform, you must meet the following eligibility requirements:</p>
        <ul>
          <li>Be at least 18 years of age</li>
          <li>
            Be a licensed real estate professional, team leader, broker, or brokerage
            representative, or be accessing the Platform on behalf of such an organization
          </li>
          <li>
            Have the legal authority to enter into these Terms on your own behalf or on behalf of an
            organization
          </li>
          <li>
            Not be located in a jurisdiction where access to or use of the Platform is prohibited by
            applicable law
          </li>
          <li>Not have had a prior account terminated by Compaire for violation of these Terms</li>
        </ul>
        <p>
          Compaire reserves the right to verify eligibility and may request documentation at any
          time. Access may be suspended pending verification.
        </p>
      </>
    ),
  },
  {
    num: "03",
    title: "Account Registration",
    id: "section-account",
    body: (
      <>
        <p>
          To access certain features of the Platform, you must create an account. You agree to
          provide accurate, current, and complete information during registration and to update
          that information to keep it accurate, current, and complete.
        </p>
        <p>By registering an account, you accept responsibility for:</p>
        <ul>
          <li>Maintaining the confidentiality of your login credentials</li>
          <li>All activity that occurs under your account</li>
          <li>Notifying Compaire immediately of any unauthorized use of your account</li>
          <li>Ensuring your account information remains accurate at all times</li>
        </ul>
        <p>
          Compaire is not liable for any loss or damage arising from your failure to comply with
          these account security obligations. You may not transfer your account to any other person
          without prior written consent from Compaire.
        </p>
      </>
    ),
    backToTop: true,
  },
  {
    num: "04",
    title: "Description of Services",
    id: "section-services",
    body: (
      <>
        <p>
          Compaire provides a career intelligence platform that enables real estate professionals to
          anonymously explore, compare, and evaluate teams and brokerages. The Platform includes
          career intelligence feeds, side-by-side comparison tools, Fit Scores, and
          direct communication channels between agents and organizations.
        </p>
        <p>
          The Platform is provided on an "as-is" and "as-available" basis. Compaire may modify,
          suspend, or discontinue any aspect of the Platform at any time, including the
          availability of any feature, database, or content.
        </p>
      </>
    ),
  },
  {
    num: "05",
    title: "Subscriptions and Billing",
    id: "section-billing",
    body: (
      <>
        <span className="legal-subsec-eyebrow">5.1 Plans</span>
        <p>Compaire offers the following subscription tiers for organizational users:</p>
        <div className="legal-def-list">
          <div className="legal-def-item">
            <span className="legal-def-term">Free</span>
            <span className="legal-def-desc">
              Available to individual agents and organizations at no cost, with no time limit.
              Agents receive core exploration, comparison, and messaging features. Organizations
              can maintain an organization profile with baseline visibility.
            </span>
          </div>
          <div className="legal-def-item">
            <span className="legal-def-term">Professional</span>
            <span className="legal-def-desc">
              Planned post-beta paid option for teams and brokerages. Founding-member rate:
              $349/month. Included features will be confirmed before paid billing begins.
            </span>
          </div>
          <div className="legal-def-item">
            <span className="legal-def-term">Enterprise</span>
            <span className="legal-def-desc">
              Planned post-beta paid option for large brokerages and multi-location organizations.
              Founding-member rate: $699/month. Included features will be confirmed before paid
              billing begins.
            </span>
          </div>
        </div>

        <span className="legal-subsec-eyebrow">5.2 Billing</span>
        <p>
          Paid plans are billed monthly or annually in advance. By providing payment information,
          you authorize Compaire to charge your payment method at the applicable rate. All fees are
          non-refundable except as expressly set forth in these Terms or as required by applicable
          law.
        </p>

        <span className="legal-subsec-eyebrow">5.3 Beta Pricing</span>
        <p>
          During the beta period, all plans are available at no cost. When the beta period ends,
          Compaire will provide at least 30 days' notice. Users who opt in to paid billing at that
          time will receive founding-member rates as described at the time of registration. No
          charges will be applied without your explicit consent.
        </p>

        <span className="legal-subsec-eyebrow">5.4 Cancellation</span>
        <p>
          You may cancel your subscription at any time from your account settings. Cancellation
          takes effect at the end of the current billing period. Following cancellation, your
          account reverts to the Free tier. Founding-member pricing is not available upon
          resubscription after cancellation.
        </p>
      </>
    ),
  },
  {
    num: "06",
    title: "Acceptable Use",
    id: "section-use",
    body: (
      <>
        <p>
          You agree to use the Platform only for lawful purposes and in accordance with these Terms.
          You must not use the Platform to transmit unsolicited communications, harvest user data,
          misrepresent your identity or affiliation, or interfere with the operation of the
          Platform.
        </p>
        <p>
          Compaire reserves the right to investigate and take appropriate legal or administrative
          action against anyone who, at Compaire's sole discretion, violates this provision.
        </p>
      </>
    ),
  },
  {
    num: "07",
    title: "User Content",
    id: "section-content",
    body: (
      <>
        <p>
          You retain ownership of any content you submit to the Platform ("User Content"). By
          submitting User Content, you grant Compaire a non-exclusive, worldwide, royalty-free
          license to use, display, and distribute that content solely for the purpose of operating
          and improving the Platform.
        </p>
        <p>
          You represent and warrant that your User Content does not infringe the rights of any
          third party and complies with all applicable laws. Compaire may remove User Content that
          violates these Terms at any time without notice.
        </p>
      </>
    ),
  },
  {
    num: "08",
    title: "Career Intent Data",
    id: "section-career-data",
    body: (
      <>
        <p>
          Career intent data — including your profile preferences, comparison activity, saved
          organizations, and Fit Score interactions — is treated with heightened confidentiality.
          This data is never shared with third parties, including organizations listed on the
          Platform, without your explicit consent.
        </p>
        <p>
          You may request deletion of your career intent data at any time by contacting{" "}
          <a href="mailto:privacy@compaire.co">privacy@compaire.co</a>. Deletion requests are
          processed within 30 days.
        </p>
      </>
    ),
  },
  {
    num: "09",
    title: "Intellectual Property",
    id: "section-ip",
    body: (
      <p>
        The Platform and its original content (excluding User Content), features, and functionality
        are and will remain the exclusive property of Compaire, Inc. and its licensors. The
        Platform is protected by copyright, trademark, and other applicable intellectual property
        laws.
      </p>
    ),
  },
  {
    num: "10",
    title: "Third-Party Services",
    id: "section-third-party",
    body: (
      <p>
        The Platform may contain links to or integrations with third-party services. Compaire does
        not control and is not responsible for the content, privacy practices, or terms of any
        third-party service. Your use of third-party services is governed by their respective terms
        and policies.
      </p>
    ),
  },
  {
    num: "11",
    title: "Disclaimers",
    id: "section-disclaimers",
    body: (
      <>
        <p>
          THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
          EXPRESS OR IMPLIED. Compaire DISCLAIMS ALL WARRANTIES INCLUDING, WITHOUT LIMITATION,
          IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
          NON-INFRINGEMENT.
        </p>
        <p>
          Compaire does not warrant that the Platform will be uninterrupted, error-free, or free
          from viruses or other harmful components. You assume full responsibility for any risks
          associated with your use of the Platform.
        </p>
      </>
    ),
  },
  {
    num: "12",
    title: "Limitation of Liability",
    id: "section-liability",
    body: (
      <>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL Compaire BE LIABLE
          FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF
          OR IN CONNECTION WITH YOUR USE OF THE PLATFORM, EVEN IF Compaire HAS BEEN ADVISED OF THE
          POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p>
          Compaire's total liability to you for any claims arising under these Terms shall not
          exceed the greater of (a) the amount you paid Compaire in the 12 months preceding the
          claim, or (b) $100.
        </p>
      </>
    ),
  },
  {
    num: "13",
    title: "Indemnification",
    id: "section-indemnification",
    body: (
      <p>
        You agree to defend, indemnify, and hold harmless Compaire and its officers, directors,
        employees, and agents from and against any claims, damages, obligations, losses,
        liabilities, costs, or debt arising from your use of the Platform, your violation of these
        Terms, or your violation of any rights of a third party.
      </p>
    ),
  },
  {
    num: "14",
    title: "Termination",
    id: "section-termination",
    body: (
      <p>
        Compaire may terminate or suspend your account and access to the Platform at any time, with
        or without cause, with or without notice. Upon termination, your right to use the Platform
        ceases immediately. Provisions of these Terms that by their nature should survive
        termination will continue in effect.
      </p>
    ),
  },
  {
    num: "15",
    title: "Governing Law and Dispute Resolution",
    id: "section-governing-law",
    body: (
      <p>
        These Terms are governed by the laws of the State of Delaware, without regard to its
        conflict of law provisions. Any dispute arising from these Terms shall be resolved by
        binding arbitration in accordance with the rules of the American Arbitration Association.
        You waive any right to a jury trial or to participate in a class action lawsuit.
      </p>
    ),
  },
  {
    num: "16",
    title: "Changes to These Terms",
    id: "section-changes",
    body: (
      <p>
        Compaire reserves the right to modify these Terms at any time. We will provide notice of
        material changes by updating the "Last Updated" date at the top of this page and, where
        appropriate, notifying you by email. Your continued use of the Platform after any changes
        constitutes your acceptance of the revised Terms.
      </p>
    ),
  },
  {
    num: "17",
    title: "Contact Us",
    id: "section-contact",
    body: (
      <>
        <p>If you have questions about these Terms of Service, please contact us:</p>
        <p>
          <strong>Compaire, Inc.</strong>
          <br />
          Legal inquiries: <a href="mailto:legal@compaire.co">legal@compaire.co</a>
          <br />
          General: <a href="mailto:hello@compaire.co">hello@compaire.co</a>
        </p>
        <p>We will respond to all legal inquiries within 10 business days.</p>
      </>
    ),
    backToTop: true,
  },
];

const TermsPage = () => {
  return (
    <>
      <CanonicalTags
        path="/terms"
        title="Terms of Service | Compaire"
        description="Read the terms for using Compaire, including account responsibilities, acceptable use, organization subscriptions and user content."
      />
      <LegalPageLayout
        eyebrow="Compaire, Inc."
        heading="Terms of Service"
        effectiveLine="Effective April 17, 2026 · Last Updated April 17, 2026"
        tocItems={TOC}
        sections={SECTIONS}
      />
    </>
  );
};

export default TermsPage;
